import { useCallback, useEffect, useState, useRef } from 'react';
import { Message } from '@oneflowab/pomes';
import { useDispatch, useSelector } from 'react-redux';
import { components } from 'react-select';
import { useHistory, useLocation } from 'react-router';
import type { OptionProps, ControlProps } from 'react-select';

import { getCurrentWorkspaceSelector } from 'reducers/app';
import { getRedirectPath } from 'workspace/folders';
import foldersReducer from 'reducers/entities/folders';
import useDebouncedCallback from 'hooks/use-debounced-callback';

import SelectField from 'components/select-field';
import MagnifyingGlassIcon from 'components/icons/magnifying-glass';
import FolderIcon from 'components/icons/folder-path';
import BlueFolderIcon from 'components/icons/folder';
import WorkspaceIcon from 'components/icons/workspace';

import style from './folder-search.module.scss';

type FolderOption = {
  resolvedPath: Oneflow.Folder['resolvedPath'];
  label: Oneflow.Folder['name'];
  value: NonNullable<Oneflow.Folder['id']>;
};

const CustomInputWithIcon = ({ children, ...props }: ControlProps) => (
  <components.Control {...props}>
    <span className={style.InputIcon} data-testid="input-icon">
      <MagnifyingGlassIcon width="14px" />
    </span>
    {children}
  </components.Control>
);

const getPath = (
  resolvedPath: Oneflow.Folder['resolvedPath'],
  workspaceName: Oneflow.Workspace['name'],
) => {
  const lastTwoPaths = resolvedPath?.slice(resolvedPath.length - 2);

  if (lastTwoPaths?.length === 1) {
    return (
      <>
        <WorkspaceIcon height="12px" width="12px" />
        <span>{workspaceName}</span>
        <span className={style.PathSeparator}>/</span>
        <FolderIcon height="12px" width="12px" />
        <span>{lastTwoPaths[0].name}</span>
      </>
    );
  }

  return (
    <>
      <span>...</span>
      <FolderIcon height="12px" width="12px" />
      <span>{lastTwoPaths?.[0].name}</span>
      <span className={style.PathSeparator}>/</span>
      <FolderIcon height="12px" width="12px" />
      <span>{lastTwoPaths?.[1].name}</span>
    </>
  );
};

interface CustomOptionProps extends OptionProps<FolderOption, false> {
  currentWorkspaceName: Oneflow.Workspace['name'];
}

const CustomOption = (props: CustomOptionProps) => {
  const { children, data, currentWorkspaceName } = props;

  return (
    <components.Option
      className={style.CustomOption}
      {...props}
    >
      <span className={style.CustomOptionContent}>
        <BlueFolderIcon height="17px" aria-hidden="true" />
        <div data-testid="option-content">{children}</div>
      </span>
      <div className={style.CustomOptionPath}>
        {getPath(data.resolvedPath, currentWorkspaceName)}
      </div>
    </components.Option>
  );
};

const DEFAULT_LOAD_LIMIT = 50;

const mapFolderToOption = (folder: Oneflow.Folder): FolderOption => ({
  resolvedPath: folder.resolvedPath,
  label: folder.name,
  value: folder.id as NonNullable<Oneflow.Folder['id']>,
});

export const FolderSearch = ({ selectedUniqueKey }: { selectedUniqueKey: number }) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const [selectedFolder, setSelectedFolder] = useState<FolderOption | null>(null);
  const ref = useRef(null);
  const currentWorkspace = useSelector(getCurrentWorkspaceSelector);
  const foldersQuery = useSelector((state) =>
    foldersReducer.getQuerySelector(state, { name: `folders-${currentWorkspace.id}` }),
  );
  const folders = useSelector((state) =>
    foldersReducer.getFoldersSelector(state, { ids: foldersQuery.result }),
  );

  const loadMoreFolders = useCallback(
    (count: number) => {
      dispatch(
        foldersReducer.queryFoldersLoadMore({
          name: `folders-${currentWorkspace.id}`,
          additionalResults: count,
        }),
      );
    },
    [currentWorkspace.id, dispatch],
  );

  const queryFolders = useCallback(
    (searchValue: string | null) => {
      dispatch(
        foldersReducer.queryFolders({
          name: `folders-${currentWorkspace.id}`,
          params: {
            limit: DEFAULT_LOAD_LIMIT,
            workspaceId: currentWorkspace.id,
            sort: 'name',
            q: searchValue || undefined,
          },
        }),
      );
    },
    [currentWorkspace.id, dispatch],
  );

  const debouncedQueryFolders = useDebouncedCallback(queryFolders, [queryFolders]);

  const onInputChange = useCallback((inputValue: string) => {
    debouncedQueryFolders(inputValue);
  }, [debouncedQueryFolders]);

  useEffect(() => {
    if (selectedUniqueKey !== selectedFolder?.value) {
      ref.current?.clearValue();
    }
  }, [selectedUniqueKey, selectedFolder]);

  return (
    <div className={style.FolderSearch}>
      <SelectField
        innerRef={ref}
        placeholder={<Message id="Select folder" comment="Placeholder for select folder field" />}
        onMenuOpen={() => queryFolders(null)}
        input={{
          name: 'folderSearch',
          value: selectedFolder,
          onChange: (value: FolderOption) => {
            setSelectedFolder(value);
            if (value) {
              history.push(getRedirectPath(location, [value.value]));
            }
          },
        }}
        components={{
          Control: CustomInputWithIcon,
          Option: (props: CustomOptionProps) => (
            <CustomOption {...props} currentWorkspaceName={currentWorkspace.name} />
          ),
        }}
        loadMoreItems={loadMoreFolders}
        additionalItemsPerLoad={DEFAULT_LOAD_LIMIT}
        totalOptionsCount={foldersQuery.count}
        isLoading={foldersQuery.loading}
        onInputChange={onInputChange}
        options={folders.map(mapFolderToOption)}
        clearable
      />
    </div>
  );
};
